/**
 * @module typingEffect
 * @description Types a list of texts into an element one character at a time, erases back
 * to the prefix the next text shares, and loops. A blinking cursor sits after the element.
 *
 * @example
 * <span aria-hidden="true">
 *   <span data-typing-placeholder>Point 1</span>
 * </span>
 * <span data-typing-points class="sr-only">, Point 2, Point 3, Point 4, Point 5</span>
 *
 * @example
 * TypingEffect.animate('[data-typing-placeholder]', '[data-typing-points]');
 */
/**
 * @typedef {object} TypingEffectOptions
 * @property {number} [typeSpeed] Base delay in ms between typed characters.
 * @property {number} [backSpeed] Base delay in ms between erased characters.
 * @property {number} [backDelay] Pause in ms after a text is fully typed.
 * @property {string} [cursorClassName] Extra classes for the cursor element.
 */

/** @type {Required<TypingEffectOptions>} */
const DEFAULT_OPTIONS = {
    typeSpeed: 80,
    backSpeed: 20,
    backDelay: 900,
    cursorClassName: '',
};

/**
 * Length of the prefix two texts share, so erasing can stop there.
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export function commonPrefixLength(a, b) {
    let length = 0;
    while (length < a.length && length < b.length && a[length] === b[length]) {
        length += 1;
    }
    return length;
}

/**
 * Random delay between `speed` and `1.5 × speed`, so the rhythm reads as human typing.
 * @param {number} speed
 * @returns {number}
 */
function humanize(speed) {
    return Math.round(Math.random() * speed / 2) + speed;
}

export class TypingEffect {
    /** @type {HTMLElement} */
    #element;

    /** @type {string[]} */
    #texts;

    /** @type {Required<TypingEffectOptions>} */
    #options;

    /** @type {HTMLSpanElement|null} */
    #cursor = null;

    /**
     * The one text node the effect writes into. Replacing textContent would insert a new node on
     * every keystroke, and a node insertion makes the style engine re-check every `:has()` rule up
     * the tree; on a long page that recalculated thousands of elements sixty times a second.
     * A change to the node's data takes the cheaper text-changed path instead.
     * @type {Text|null}
     */
    #textNode = null;

    /** @type {ReturnType<typeof setTimeout>|undefined} */
    #timer;

    /**
     * @param {HTMLElement} element Element whose text content gets typed.
     * @param {string[]} texts Texts to cycle through, in order.
     * @param {TypingEffectOptions} [options]
     */
    constructor(element, texts, options = {}) {
        this.#element = element;
        this.#texts = texts.map(text => text.trim()).filter(text => text !== '');
        this.#options = {...DEFAULT_OPTIONS, ...options};
    }

    start() {
        if (this.#texts.length === 0) {
            return;
        }

        this.#textNode = document.createTextNode('');
        this.#element.replaceChildren(this.#textNode);
        this.#insertCursor();
        this.#type(0, 0);
    }

    /** @param {string} text */
    #show(text) {
        if (this.#textNode?.parentNode !== this.#element) {
            this.#textNode = document.createTextNode('');
            this.#element.replaceChildren(this.#textNode);
        }
        this.#textNode.data = text;
    }

    destroy() {
        clearTimeout(this.#timer);
        this.#cursor?.remove();
        this.#cursor = null;
    }

    /**
     * Wire a placeholder and its screen-reader list into one effect. The placeholder's
     * current text becomes the first entry, and the list is rewritten to include it so
     * assistive tech reads the full set once.
     * @param {string|HTMLElement} placeholderSelector
     * @param {string|HTMLElement} pointsSelector Element with ", Point 2, Point 3" text.
     * @param {TypingEffectOptions} [options]
     * @returns {TypingEffect|null}
     */
    static animate(placeholderSelector, pointsSelector, options = {}) {
        const placeholder = typeof placeholderSelector === 'string'
            ? document.querySelector(placeholderSelector)
            : placeholderSelector;
        const points = typeof pointsSelector === 'string'
            ? document.querySelector(pointsSelector)
            : pointsSelector;

        if (!placeholder || !points) {
            return null;
        }

        // Split on any whitespace after the comma: the markup wraps long point lists over several lines.
        const texts = points.textContent.split(/,\s+/);
        texts.splice(0, 1, placeholder.textContent);
        points.textContent = texts.join(', ');

        const effect = new TypingEffect(placeholder, texts, options);
        effect.start();

        return effect;
    }

    #insertCursor() {
        const cursor = document.createElement('span');
        cursor.className = `typingEffect__cursor ${this.#options.cursorClassName}`.trim();
        cursor.setAttribute('aria-hidden', 'true');
        cursor.textContent = '|';
        this.#element.after(cursor);
        this.#cursor = cursor;
    }

    /** @param {boolean} blinking */
    #setCursorBlinking(blinking) {
        this.#cursor?.classList.toggle('typingEffect__cursor--blink', blinking);
    }

    /**
     * @param {number} index Position of the text in the list.
     * @param {number} position Number of characters shown.
     */
    #type(index, position) {
        const text = this.#texts[index];
        this.#show(text.slice(0, position));

        if (position < text.length) {
            this.#setCursorBlinking(false);
            this.#timer = setTimeout(() => this.#type(index, position + 1), humanize(this.#options.typeSpeed));
            return;
        }

        this.#setCursorBlinking(true);

        if (this.#texts.length === 1) {
            return;
        }

        this.#timer = setTimeout(() => this.#erase(index, position), this.#options.backDelay);
    }

    /**
     * @param {number} index Position of the text in the list.
     * @param {number} position Number of characters shown.
     */
    #erase(index, position) {
        const nextIndex = (index + 1) % this.#texts.length;
        const keep = commonPrefixLength(this.#texts[index], this.#texts[nextIndex]);

        if (position > keep) {
            this.#setCursorBlinking(false);
            this.#show(this.#texts[index].slice(0, position - 1));
            this.#timer = setTimeout(() => this.#erase(index, position - 1), humanize(this.#options.backSpeed));
            return;
        }

        this.#type(nextIndex, position);
    }
}
